import { XRPC, simpleFetchHandler, XRPCError } from '@atcute/client';
import { tokenize } from '@atcute/bluesky-richtext-parser';

const ATTR_REGEX = /[&"<]/g;
const CONTENT_REGEX = /[&<]/g;

/**
 * @template V
 * @param {V} value
 * @param {boolean} [is_attr]
 */
function escape_html(value, is_attr) {
	const str = String(value ?? '');

	const pattern = is_attr ? ATTR_REGEX : CONTENT_REGEX;
	pattern.lastIndex = 0;

	let escaped = '';
	let last = 0;

	while (pattern.test(str)) {
		const i = pattern.lastIndex - 1;
		const ch = str[i];
		escaped += str.substring(last, i) + (ch === '&' ? '&amp;' : ch === '"' ? '&quot;' : '&lt;');
		last = i + 1;
	}

	return escaped + str.substring(last);
}

/**
 * @template V
 * @param {string} name
 * @param {V} value
 * @param {boolean} [is_boolean]
 * @returns {string}
 */
function attr(name, value, is_boolean = false) {
	if (value == null || (!value && is_boolean) || (value === '' && name === 'class')) return '';
	const normalized = value;
	const assignment = is_boolean ? '' : `="${escape_html(normalized, true)}"`;
	return ` ${name}${assignment}`;
}

/** @import { ComponentType, SvelteComponent } from 'svelte' */
/** @import { Component, Payload, RenderOutput } from '#server' */
/** @import { Store } from '#shared' */

/**
 * Only available on the server and when compiling with the `server` option.
 * Takes a component and returns an object with `body` and `head` properties on it, which you can use to populate the HTML when server-rendering your app.
 * @template {Record<string, any>} Props
 * @param {import('svelte').Component<Props> | ComponentType<SvelteComponent<Props>>} component
 * @param {{ props?: Omit<Props, '$$slots' | '$$events'>; context?: Map<any, any> }} [options]
 * @returns {RenderOutput}
 */
function render(component, options) {
	/** @type {Payload} */
	const payload = { out: '' };
	component(payload, options?.props ?? {});

	return {
		head: '',
		body: payload.out
	};
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function stringify(value) {
	return typeof value === 'string' ? value : value == null ? '' : value + '';
}

/** @param {any} array_like_or_iterator */
function ensure_array_like(array_like_or_iterator) {
	return array_like_or_iterator;
}

const DEFAULT_APPVIEW_URL = "https://public.api.bsky.app";
const NO_UNAUTHENTICATED_LABEL = "!no-unauthenticated";

function Embed_frame($$payload, $$props) {
	

	const { children } = $$props;

	$$payload.out += `<div class="bluesky-embed s-dgn7w7">`;
	children($$payload);
	$$payload.out += `</div>`;
	
}

const getProfileUrl = (author) => {
  return `https://bsky.app/profile/${author}`;
};
const getHashtagUrl = (tag) => {
  return `https://bsky.app/hashtag/${tag}`;
};

const FlagsNone = 0;
const FlagsNoSelf = 1 << 0;
const LABEL_MAPPING = {
  "!hide": {
    name: `Hidden by moderators`,
    flags: FlagsNoSelf
  },
  "!warn": {
    name: `Content warning`,
    flags: FlagsNoSelf
  },
  porn: {
    name: `Adult content`,
    flags: FlagsNone
  },
  sexual: {
    name: `Sexually suggestive`,
    flags: FlagsNone
  },
  "graphic-media": {
    name: `Graphic media`,
    flags: FlagsNone
  },
  nudity: {
    name: `Nudity`,
    flags: FlagsNone
  }
};
const findLabel = (labels, authorDid) => {
  if (labels?.length) {
    for (let idx = 0, len = labels.length; idx < len; idx++) {
      const label = labels[idx];
      const val = label.val;
      if (!(val in LABEL_MAPPING)) {
        continue;
      }
      const def = LABEL_MAPPING[val];
      if (def.flags & FlagsNoSelf && label.src === authorDid) {
        continue;
      }
      return def;
    }
  }
};

const long = new Intl.NumberFormat("en-US");
const compact = new Intl.NumberFormat("en-US", { notation: "compact" });
const formatCompactNumber = (value) => {
  if (value < 1e3) {
    return "" + value;
  }
  if (value < 1e5) {
    return long.format(value);
  }
  return compact.format(value);
};

const HTTP_RE = /^https?:\/\//;

function Richtext_raw_renderer($$payload, $$props) {
	

	const { text } = $$props;
	const each_array = ensure_array_like(tokenize(text));

	$$payload.out += `<p class="rich-text is-small s-1ea7h2m">`;

	for (let $$index = 0, $$length = each_array.length; $$index < $$length; $$index++) {
		let token = each_array[$$index];

		if (token.type === 'autolink') {
			
			$$payload.out += `<a target="_blank"${attr("href", token.url)} rel="noopener nofollow" class="link s-1ea7h2m">${escape_html(token.raw.replace(HTTP_RE, ''))}</a>`;
		} else {
			

			if (token.type === 'mention') {
				
				$$payload.out += `<a target="_blank"${attr("href", getProfileUrl(token.handle))} class="mention s-1ea7h2m">${escape_html(token.raw)}</a>`;
			} else {
				

				if (token.type === 'topic') {
					
					$$payload.out += `<a target="_blank"${attr("href", getHashtagUrl(token.name))} class="hashtag s-1ea7h2m">${escape_html(token.raw)}</a>`;
				} else {
					
					$$payload.out += escape_html(token.raw);
				}

				
			}

			
		}

		
	}

	$$payload.out += `</p>`;
	
}

function Profile_card($$payload, $$props) {
	

	const { profile } = $$props;
	const url = getProfileUrl(profile.did);
	const isBlurred = findLabel(profile.labels, profile.did);

	$$payload.out += `<div class="profile-card has-banner s-2b3fq6"><div class="banner-wrapper s-2b3fq6">`;

	if (profile.banner) {
		
		$$payload.out += `<img loading="lazy"${attr("src", profile.banner)} alt=""${attr("class", `${stringify(`banner` + (isBlurred ? ` is-blurred` : ``))} s-2b3fq6`)}>`;
	}

	$$payload.out += `</div> <div class="contents s-2b3fq6"><div class="header s-2b3fq6"><a${attr("href", url)} target="_blank" class="avatar-wrapper s-2b3fq6">`;

	if (profile.avatar) {
		
		$$payload.out += `<img loading="lazy"${attr("src", profile.avatar)} alt=""${attr("class", `${stringify(`avatar` + (isBlurred ? ` is-blurred` : ``))} s-2b3fq6`)}>`;
	}

	$$payload.out += `</a> <div class="actions s-2b3fq6"><a${attr("href", url)} target="_blank" class="follow-button s-2b3fq6"><svg class="icon s-2b3fq6" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="square" stroke-width="2" d="M12 4v8m0 0v8m0-8H4m8 0h8"></path></svg> <span>Follow</span></a> <svg class="logo s-2b3fq6" fill="none" viewBox="0 0 320 286"><path fill="#0A7AFF" d="M69.364 19.146c36.687 27.806 76.147 84.186 90.636 114.439 14.489-30.253 53.948-86.633 90.636-114.439C277.107-.917 320-16.44 320 32.957c0 9.865-5.603 82.875-8.889 94.729-11.423 41.208-53.045 51.719-90.071 45.357 64.719 11.12 81.182 47.953 45.627 84.785-80 82.874-106.667-44.333-106.667-44.333s-26.667 127.207-106.667 44.333c-35.555-36.832-19.092-73.665 45.627-84.785-37.026 6.362-78.648-4.149-90.071-45.357C5.603 115.832 0 42.822 0 32.957 0-16.44 42.893-.917 69.364 19.147Z"></path></svg></div></div> <div class="name-wrapper"><p dir="auto" class="display-name s-2b3fq6">${escape_html(profile.displayName?.trim() || profile.handle.slice(0, 64))}</p> <p class="handle s-2b3fq6">@${escape_html(profile.handle)}</p></div> <div class="stats s-2b3fq6"><span class="stat-entry s-2b3fq6"><span class="stat-count s-2b3fq6">${escape_html(formatCompactNumber(profile.followersCount || 0))}</span> <span>${escape_html(profile.followersCount === 1 ? `Follower` : `Followers`)}</span></span> <span class="stat-entry s-2b3fq6"><span class="stat-count s-2b3fq6">${escape_html(formatCompactNumber(profile.followsCount || 0))}</span> <span>Following</span></span></div> `;

	if (profile.description?.trim()) {
		
		Richtext_raw_renderer($$payload, { text: profile.description });
	}

	$$payload.out += `</div></div>`;
	
}

function Message($$payload, msg) {
	Embed_frame($$payload, {
		children: ($$payload) => {
			$$payload.out += `<div class="message s-ibmh3w">${escape_html(msg)}</div>`;
		},
		
	});
}

function Bluesky_profile_card($$payload, $$props) {
	

	const { profile, allowUnauthenticated } = $$props;
	const isPwiForbidden = !allowUnauthenticated && profile?.labels?.some((label) => label.val === NO_UNAUTHENTICATED_LABEL);

	if (profile === null) {
		
		Message($$payload, `The profile can't be found, it may have been deleted.`);
	} else {
		

		if (isPwiForbidden) {
			
			Message($$payload, `The user has requested for their profile to not be displayed on external sites.`);
		} else {
			

			Embed_frame($$payload, {
				children: ($$payload) => {
					Profile_card($$payload, { profile });
				},
				
			});
		}

		
	}

	
	
}

const fetchProfileCard = async (opts) => {
  const actor = opts.actor;
  const allowUnauthenticated = opts.allowUnauthenticated ?? false;
  const rpc = new XRPC({ handler: simpleFetchHandler({ service: opts.serviceUri ?? DEFAULT_APPVIEW_URL }) });
  const { data: profile } = await rpc.get("app.bsky.actor.getProfile", {
    signal: opts.signal,
    params: { actor }
  }).catch((err) => {
    if (err instanceof XRPCError) {
      if (err.kind === "InvalidRequest" && err.description === "Profile not found") {
        return { data: null };
      }
    }
    return Promise.reject(err);
  });
  return { profile, allowUnauthenticated };
};
const renderProfileCard = (data) => {
  return render(Bluesky_profile_card, { props: data }).body;
};

export { fetchProfileCard, renderProfileCard };
